import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import { API } from '../config';
import {
  fmt, fmtResa, percento, segmentiArchivio, quotaNonTim, righeEsiti, GRUPPI_ESITO,
  personeFisiche, nomeLuogo, segmentiIdentita, raggiSegmenti,
} from '../pages/campagneStatData';
import { eCerved, eGenerica } from '../pages/campagneData';
import './campagneStatistiche.css';

/* La scheda «Statistiche» delle Campagne: quanto rendono le liste TIM, dove, e cosa ne e'
   nato in archivio. Il perimetro e' quello del selettore in cima alla pagina (una campagna o
   tutte); l'elenco delle campagne della risposta invece e' SEMPRE completo, perche' serve a
   confrontare le liste fra loro.

   Grafici con recharts, la stessa libreria della sezione Analisi. Dove una tabella dice le
   cose meglio (confronto fra campagne, regioni e province) si usa la tabella; niente torte.
   Ogni grafico ha i suoi numeri scritti accanto: il colore non e' mai l'unico modo di
   leggere un valore. */

const fmtData = (at) => {
  if (!at) return '';
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('it-IT',
    { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const Card = ({ titolo, sotto, destra, children, className = '' }) => (
  <section className={`cst-card ${className}`}>
    <div className="cst-card-head">
      <div>
        <h3 className="cst-card-tit">{titolo}</h3>
        {sotto && <div className="cst-card-sotto">{sotto}</div>}
      </div>
      {destra}
    </div>
    {children}
  </section>
);

/** Numero chiave: etichetta, valore, una riga di contesto. */
const Tile = ({ label, valore, nota, forte }) => (
  <div className={`cst-tile${forte ? ' forte' : ''}`}>
    <div className="cst-tile-label">{label}</div>
    <div className="cst-tile-val">{valore}</div>
    {nota && <div className="cst-tile-nota">{nota}</div>}
  </div>
);

/** Barra di misura (resa, quota): la traccia e' un passo piu' chiaro dello stesso colore. */
const Metro = ({ valore, colore = 'var(--accent)' }) => (
  <span className="cst-metro" style={{ '--metro': colore }}>
    <span style={{ width: `${Math.max(0, Math.min(1, valore || 0)) * 100}%` }} />
  </span>
);

const BoxTooltip = ({ children }) => <div className="cst-tip">{children}</div>;

/** Una barra orizzontale composta (parti di un tutto), con la legenda scritta sotto: ogni
    voce ha il suo quadratino, il numero e la quota. */
function BarraComposta({ segmenti, totale, nomeTotale }) {
  const riga = Object.fromEntries(segmenti.map((s) => [s.key, s.valore]));
  const raggi = raggiSegmenti(segmenti);
  return (
    <>
      <div className="cst-composta">
        <ResponsiveContainer width="100%" height={28}>
          <BarChart layout="vertical" data={[{ nome: nomeTotale, ...riga }]}
            margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
            <XAxis type="number" hide domain={[0, Math.max(1, totale)]} />
            <YAxis type="category" dataKey="nome" hide />
            <Tooltip cursor={false} content={() => (
              <BoxTooltip>
                <b>{nomeTotale}: {fmt(totale)}</b>
                {segmenti.map((s) => (
                  <div key={s.key} className="cst-tip-riga">
                    <i style={{ background: s.colore }} />{s.label}
                    <span>{fmt(s.valore)} · {percento(s.valore, totale) || '0%'}</span>
                  </div>
                ))}
              </BoxTooltip>
            )} />
            {segmenti.map((s, i) => (
              <Bar key={s.key} dataKey={s.key} stackId="tutto" fill={s.colore} barSize={24}
                stroke="var(--surface)" strokeWidth={2} radius={raggi[i]}
                isAnimationActive={false} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="cst-legenda">
        {segmenti.map((s) => (
          <div key={s.key} className="cst-leg-voce" title={s.spiega}>
            <i style={{ background: s.colore }} />
            <span className="cst-leg-label">{s.label}</span>
            <b>{fmt(s.valore)}</b>
            <span className="cst-leg-quota">{percento(s.valore, totale) || '0%'}</span>
          </div>
        ))}
      </div>
    </>
  );
}

/* ── 2. Il dato per cui esiste tutto: TIM o NON TIM ── */
function Esito456({ archivio }) {
  const a = archivio || {};
  const nati = a.nati || 0;
  const qNonTim = quotaNonTim(a);
  return (
    <Card className="cst-hero" titolo="Dei numeri ricostruiti: TIM o NON TIM"
      sotto={nati
        ? `${fmt(nati)} ${nati === 1 ? 'numero entrato' : 'numeri entrati'} in archivio dalle liste: ecco cosa dice il 456`
        : 'Ancora nessun numero ricostruito è entrato in archivio'}>
      {nati === 0 ? (
        <div className="cst-vuoto">
          Quando l’indagine ricostruisce un numero nasce il record in archivio, e il 456 dice se
          la linea è TIM o NON TIM. Qui comparirà il conto.
        </div>
      ) : (
        <>
          <div className="cst-hero-numeri">
            <div className="cst-hero-fig">
              <span className="cst-hero-label"><i style={{ background: 'var(--color-non-tim)' }} />NON TIM</span>
              <span className="cst-hero-val">{fmt(a.non_tim)}</span>
              <span className="cst-hero-nota">
                {qNonTim === null ? 'nessun numero ancora verificato'
                  : `${fmtResa(qNonTim)} dei numeri già verificati`}
              </span>
            </div>
            <div className="cst-hero-fig">
              <span className="cst-hero-label"><i style={{ background: 'var(--color-tim)' }} />TIM</span>
              <span className="cst-hero-val medio">{fmt(a.tim)}</span>
              <span className="cst-hero-nota">già clienti TIM</span>
            </div>
            <div className="cst-hero-fig">
              <span className="cst-hero-label"><i style={{ background: 'var(--faint)' }} />In attesa del 456</span>
              <span className="cst-hero-val medio">{fmt(a.da_verificare)}</span>
              <span className="cst-hero-nota">
                {a.da_verificare ? 'non ancora verificati' : 'tutti verificati'}
              </span>
            </div>
          </div>
          <BarraComposta segmenti={segmentiArchivio(a)} totale={nati} nomeTotale="Nati in archivio" />
        </>
      )}
    </Card>
  );
}

/* ── 3. Resa per campagna ── */
function ResaCampagne({ campagne, campagnaId, onScegli }) {
  const elenco = campagne || [];
  const una = elenco.length === 1;
  // il tipo di lista si scrive solo quando ce ne sono di due tipi: con sole liste TIM la
  // tabella resta com'era
  // il distintivo del tipo compare solo se le liste non sono tutte uguali
  const misti = elenco.some(eCerved) || elenco.some(eGenerica);
  return (
    <Card titolo="Resa per campagna"
      sotto={una
        ? 'Una sola lista caricata: il confronto comincia con la prossima.'
        : 'Numeri ricostruiti su righe indagate, lista per lista. Clicca una lista per guardare solo lei.'}
      destra={campagnaId ? (
        <button type="button" className="camp-azzera" onClick={() => onScegli('')}>
          Tutte le campagne
        </button>
      ) : null}>
      <div className="cst-tabwrap">
        <table className="cst-tab">
          <thead>
            <tr>
              <th>Campagna</th>
              <th className="n">Righe</th>
              <th className="n">Indagate</th>
              <th className="n">Risolte</th>
              <th className="cst-col-resa">Resa</th>
              <th className="n">Già lavorate</th>
              <th className="n">Già in archivio</th>
            </tr>
          </thead>
          <tbody>
            {elenco.map((c) => (
              <tr key={c.id} className={`cliccabile${c.id === campagnaId ? ' sel' : ''}`}
                onClick={() => onScegli(c.id === campagnaId ? '' : c.id)}
                title={c.id === campagnaId ? 'Torna a tutte le liste' : 'Guarda solo questa lista'}>
                <td>
                  <span className="cst-nome">{c.nome || 'senza nome'}</span>
                  {misti && (
                    <span className={`camp-tipo${eCerved(c) || eGenerica(c) ? '' : ' tim'}`}
                      title={eCerved(c)
                        ? 'Lista CERVED: senza numero, con la partita IVA certa'
                        : eGenerica(c)
                          ? 'Lista da import: un nome, un comune e al massimo un cellulare'
                          : 'Lista TIM: col numero troncato'}>
                      {eCerved(c) ? 'CERVED' : eGenerica(c) ? 'DA IMPORT' : 'TIM'}
                    </span>
                  )}
                  {fmtData(c.caricata_at) && <span className="cst-data">{fmtData(c.caricata_at)}</span>}
                </td>
                <td className="n">{fmt(c.righe)}</td>
                <td className="n">{fmt(c.indagate)}</td>
                <td className="n">{fmt(c.risolte)}</td>
                <td className="cst-col-resa">
                  {c.indagate ? (
                    <span className="cst-resa"><Metro valore={c.resa} /><b>{fmtResa(c.resa, c.indagate)}</b></span>
                  ) : <span className="cst-niente">nessuna indagata</span>}
                </td>
                <td className="n">{fmt(c.gia_lavorate)}</td>
                <td className="n">{fmt(c.gia_in_archivio)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ── 4. Dove finiscono le righe ── */
function Esiti({ esiti, righe }) {
  const dati = righeEsiti(esiti, righe);
  const gruppi = GRUPPI_ESITO.filter((g) => dati.some((d) => d.gruppo === g.key));
  if (!dati.length) {
    return <Card titolo="Dove finiscono le righe"><div className="cst-vuoto">Nessuna riga.</div></Card>;
  }
  return (
    <Card titolo="Dove finiscono le righe"
      sotto={`Le ${fmt(righe)} righe, per esito: scartate all’ingresso, indagate, ancora da fare.`}
      destra={(
        <div className="cst-legenda cst-legenda-alto">
          {gruppi.map((g) => (
            <span key={g.key} className="cst-leg-voce"><i style={{ background: g.colore }} />{g.label}</span>
          ))}
        </div>
      )}>
      <div style={{ height: dati.length * 30 + 12 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart layout="vertical" data={dati} margin={{ top: 4, right: 96, bottom: 4, left: 0 }}
            barCategoryGap={6}>
            <XAxis type="number" hide domain={[0, 'dataMax']} />
            <YAxis type="category" dataKey="breve" width={200} interval={0} tickLine={false}
              axisLine={{ stroke: 'var(--line)' }}
              tick={{ fontSize: 12, fill: 'var(--text)' }} />
            <Tooltip cursor={{ fill: 'var(--surface-2)' }} content={({ active, payload }) => {
              const d = active && payload && payload[0] && payload[0].payload;
              if (!d) return null;
              return (
                <BoxTooltip>
                  <div className="cst-tip-riga"><i style={{ background: d.colore }} />{d.gruppoLabel}</div>
                  <b>{d.lungo}</b>
                  <div>{fmt(d.quante)} righe · {percento(d.quante, righe)} del totale</div>
                </BoxTooltip>
              );
            }} />
            <Bar dataKey="quante" barSize={16} radius={[0, 4, 4, 0]} isAnimationActive={false}>
              {dati.map((d) => <Cell key={d.chiave} fill={d.colore} />)}
              <LabelList dataKey="quante" position="right"
                content={({ x, y, width, height, index }) => {
                  const d = dati[index];
                  if (!d) return null;
                  return (
                    <text x={x + width + 8} y={y + height / 2} dy="0.35em"
                      className="cst-bar-val">
                      {fmt(d.quante)}
                      <tspan className="cst-bar-quota"> {percento(d.quante, righe)}</tspan>
                    </text>
                  );
                }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

/* ── 5. Persone fisiche ── */
function PersoneFisiche({ stat, campagnaId }) {
  const p = personeFisiche(stat);
  const qui = stat?.totali?.persone_fisiche || 0;
  return (
    <Card titolo="Persone fisiche"
      sotto="Non si lavorano: vengono scartate all’ingresso e conservate, e si possono riarmare.">
      <div className="cst-pf">
        <div className="cst-pf-fig">
          <span className="cst-hero-val medio">{fmt(p.tutte)}</span>
          <span className="cst-hero-nota">
            su tutte le campagne{p.righeTutte ? ` · ${percento(p.tutte, p.righeTutte)} delle righe` : ''}
          </span>
          {campagnaId && (
            <span className="cst-pf-qui">in questa lista: <b>{fmt(qui)}</b></span>
          )}
        </div>
        <ul className="cst-pf-elenco">
          {p.perCampagna.map((c) => (
            <li key={c.id} className={c.id === campagnaId ? 'sel' : ''}>
              <span className="cst-nome">{c.nome || 'senza nome'}</span>
              <Metro valore={c.quota} colore="var(--muted)" />
              <b>{fmt(c.quante)}</b>
              <span className="cst-leg-quota">{c.righe ? percento(c.quante, c.righe) : '—'}</span>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

/* ── 6. Regioni e province ── */
function Regioni({ regioni }) {
  const [aperte, setAperte] = useState(() => new Set());
  const elenco = regioni || [];
  const apri = (r) => setAperte((s) => {
    const n = new Set(s);
    if (n.has(r)) n.delete(r); else n.add(r);
    return n;
  });
  const cella = (x) => (
    <>
      <td className="n">{fmt(x.righe)}</td>
      <td className="n">{fmt(x.indagate)}</td>
      <td className="n">{fmt(x.risolte)}</td>
      <td className="cst-col-resa">
        {x.indagate ? (
          <span className="cst-resa"><Metro valore={x.resa} /><b>{fmtResa(x.resa, x.indagate)}</b></span>
        ) : <span className="cst-niente">—</span>}
      </td>
    </>
  );
  return (
    <Card titolo="Regioni e province lavorate"
      sotto="Clicca una regione per aprire le sue province.">
      {elenco.length === 0 ? <div className="cst-vuoto">Nessuna riga.</div> : (
        <div className="cst-tabwrap">
          <table className="cst-tab">
            <thead>
              <tr>
                <th>Regione / provincia</th>
                <th className="n">Righe</th>
                <th className="n">Indagate</th>
                <th className="n">Risolte</th>
                <th className="cst-col-resa">Resa</th>
              </tr>
            </thead>
            <tbody>
              {elenco.map((r) => {
                const aperta = aperte.has(r.regione);
                const province = r.province || [];
                return (
                  <React.Fragment key={r.regione}>
                    <tr className="cliccabile cst-regione" onClick={() => apri(r.regione)}>
                      <td>
                        <button type="button" className="cst-apri" aria-expanded={aperta}
                          onClick={(e) => { e.stopPropagation(); apri(r.regione); }}>
                          <span className={`cst-freccia${aperta ? ' giu' : ''}`} aria-hidden="true">▸</span>
                          {nomeLuogo(r.regione)}
                        </button>
                        <span className="cst-data">
                          {province.length} {province.length === 1 ? 'provincia' : 'province'}
                        </span>
                      </td>
                      {cella(r)}
                    </tr>
                    {aperta && province.map((p) => (
                      <tr key={`${r.regione}|${p.provincia}`} className="cst-provincia">
                        {/* sotto "NON ASSEGNATA" c'e' la provincia com'era scritta nel file
                            (una sigla, un refuso): si mostra tale e quale, non ritoccata */}
                        {r.regione === 'NON ASSEGNATA' && p.provincia !== 'NON ASSEGNATA' ? (
                          <td title="Provincia non riconosciuta: così com’è scritta nel file">{p.provincia}</td>
                        ) : <td>{nomeLuogo(p.provincia)}</td>}
                        {cella(p)}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* ── 7. Quanto sono solide le identita' ── */
function Identita({ identita }) {
  const segmenti = segmentiIdentita(identita);
  const tot = segmenti.reduce((n, s) => n + s.valore, 0);
  return (
    <Card titolo="Quanto sono solide le identità"
      sotto="Fra le righe risolte: come si è verificato che la scheda di Maps fosse davvero quell’azienda.">
      {tot === 0 ? <div className="cst-vuoto">Ancora nessuna riga risolta.</div> : (
        <BarraComposta segmenti={segmenti} totale={tot} nomeTotale="Righe risolte" />
      )}
    </Card>
  );
}

export default function CampagneStatistiche({ campagnaId, aggiorna, onScegliCampagna }) {
  const [stat, setStat] = useState(null);
  const [stato, setStato] = useState('carico');   // 'carico' | 'ok' | 'errore'
  const [giro, setGiro] = useState(0);
  const riprova = useCallback(() => setGiro((g) => g + 1), []);

  /* Cambiata campagna (o riletto l'elenco) si richiede. Durante la rilettura resta a schermo
     il quadro di prima, attenuato: niente pagina che salta a "Carico…" ogni volta. `vivo`
     scarta la risposta di un perimetro gia' superato da un altro. */
  useEffect(() => {
    let vivo = true;
    setStato('carico');
    const q = campagnaId ? `?campagna_id=${encodeURIComponent(campagnaId)}` : '';
    axios.get(`${API}/campagne/statistiche${q}`)
      .then((r) => { if (vivo) { setStat(r.data || null); setStato('ok'); } })
      .catch(() => { if (vivo) setStato('errore'); });
    return () => { vivo = false; };
  }, [campagnaId, aggiorna, giro]);

  if (!stat) {
    return stato === 'errore' ? (
      <div className="camp-msg camp-msg-err">
        Non riesco a leggere le statistiche.{' '}
        <button type="button" className="camp-azzera" onClick={riprova}>Riprova</button>
      </div>
    ) : <div className="camp-msg">Calcolo le statistiche…</div>;
  }

  const t = stat.totali || {};
  return (
    <div className={`cst${stato === 'carico' ? ' cst-aggiorno' : ''}`} aria-busy={stato === 'carico'}>
      {stato === 'errore' && (
        <div className="camp-box camp-box-warn">
          Non sono riuscito ad aggiornare le statistiche: quelle qui sotto sono di prima.{' '}
          <button type="button" className="camp-azzera" onClick={riprova}>Riprova</button>
        </div>
      )}

      {/* ── 1. I numeri chiave ── */}
      <div className="cst-tiles">
        <Tile label="Righe caricate" valore={fmt(t.righe)}
          nota={t.persone_fisiche || t.gia_lavorate
            ? `${fmt(t.persone_fisiche)} persone fisiche · ${fmt(t.gia_lavorate)} già lavorate`
            : null} />
        <Tile label="Indagate" valore={fmt(t.indagate)}
          nota={t.righe ? `${percento(t.indagate, t.righe)} delle righe` : null} />
        <Tile label="Numeri ricostruiti" valore={fmt(t.risolte)}
          nota={t.non_risolte ? `${fmt(t.non_risolte)} indagate senza esito` : null} />
        <Tile label="Resa" valore={fmtResa(t.resa, t.indagate)} forte
          nota={t.indagate ? 'numeri ricostruiti su righe indagate' : 'nessuna riga ancora indagata'} />
      </div>

      {t.righe === 0 ? (
        <div className="camp-msg">Questa campagna non ha righe.</div>
      ) : (
        <>
          <Esito456 archivio={stat.archivio} />
          <ResaCampagne campagne={stat.campagne} campagnaId={campagnaId}
            onScegli={onScegliCampagna} />
          <Esiti esiti={stat.esiti} righe={t.righe} />
          <PersoneFisiche stat={stat} campagnaId={campagnaId} />
          <Regioni regioni={stat.regioni} />
          <Identita identita={stat.identita} />
        </>
      )}
    </div>
  );
}
